import { createSlice } from '@reduxjs/toolkit'
import { bbCrossGamePlacedBet, foggyCrossGameAutoBet, getMyBetsCrossGame, bbGameOpenTile, bbCrossGameCashOut, getBbCrossGamePreviousRoundState, bbCrossGameTopBets, bbCrossGameAllBets } from 'games/BbCrossGame/slice-thunks/bbCrossGame.thunk'
import { minus, plus } from 'number-precision'
import pickBy from 'lodash/pickBy'
import { BET_RESULT } from 'utils/constants/index'
import { DEFAULT_MINE_GAME_AUTOBET_ROUNDS } from '../GameTheme/BbCross/bbCross.constants'
import { getPrecision } from 'helpers/common.helpers'

const initialState = {
  loading: false,
  currentPlacedBet: null,
  currentSelectedTiles: [],
  openTileRequestId: null,
  betLock: false,
  gameEnd: false,
  myBetsDataLoading: false,
  myBetsData: [],
  topBetsData: [],
  allBetsData: [],
  autoBetNoOfRounds: DEFAULT_MINE_GAME_AUTOBET_ROUNDS,
  autoBetCurrentPlacedBet: null,
  isAutoPlayStarted: false,
  autoBetSuccess: false,
  strategyInputs: {
    stopOnProfit: 0,
    stopOnLoss: 0,
    maxBetAmount: 0,
    onWinEnabled: false,
    onWinIncreasePercent: 0,
    onLossEnabled: false,
    onLossIncreasePercent: 0
  },
  strategyWatchers: {
    totalBetAmount: 0,
    autoBetAmount: 0,
    initialAutoBetAmount: 0,
    netReturn: 0
  },
  isLastWin: false,
  isLastLoss: false,
  liveStats: {
    totalProfit: 0,
    totalWagered: 0,
    totalWins: 0,
    totalLost: 0
  },
  minesVolume: 1,
  hasPreviousRoundCalled: false
}

const {
  actions: {
    setAutoBetNoOfRounds,
    setInitialAutoBetAmount,
    addCurrentSelectedTiles,
    removeCurrentSelectedTiles,
    callBbCrossGameRoundEnd,
    stopAutoBet,
    setAutoPlayEndAfterAnimation,
    setStrategyInputs,
    setAutoBetAmount,
    setRoundResult,
    setLiveStats,
    resetLiveStats,
    setMinesVolume
  },
  reducer
} = createSlice({
  name: 'bbCrossGame',
  initialState,
  reducers: {
    setLiveStats: (state, action) => {
      const statistics = action.payload?.statistics
      return {
        ...state,
        liveStats: {
          ...statistics
        }
      }
    },
    resetLiveStats: (state, action) => {
      return {
        ...state,
        strategyWatchers: { ...initialState.strategyWatchers, autoBetAmount: state.strategyWatchers.autoBetAmount },
        liveStats: initialState.liveStats
      }
    },
    setAutoPlayEndAfterAnimation: (state, action) => {
      return {
        ...state,
        autoBetCurrentPlacedBet: null,
        autoBetSuccess: false,
        isLastWin: false,
        isLastLoss: false
      }
    },
    setAutoBetNoOfRounds: (state, action) => {
      return {
        ...state,
        autoBetNoOfRounds: action.payload
      }
    },
    setRoundResult: (state, action) => {
      return {
        ...state,
        isLastWin: action.payload.isLastWin,
        isLastLoss: action.payload.isLastLoss
      }
    },
    setStrategyInputs: (state, action) => {
      return {
        ...state,
        strategyInputs: action.payload
      }
    },
    callBbCrossGameRoundEnd: (state, action) => {
      return {
        ...state,
        betLock: false,
        gameEnd: false,
        currentSelectedTiles: initialState.currentSelectedTiles,
        currentPlacedBet: initialState.currentPlacedBet,
        isLastWin: false,
        isLastLoss: false
      }
    },
    setAutoBetAmount: (state, action) => {
      return {
        ...state,
        strategyWatchers: { ...state.strategyWatchers, autoBetAmount: action.payload }
      }
    },
    setInitialAutoBetAmount: (state, action) => {
      return {
        ...state,
        strategyWatchers: { ...state.strategyWatchers, initialAutoBetAmount: action.payload }
      }
    },
    addCurrentSelectedTiles: (state, action) => {
      return {
        ...state,
        currentSelectedTiles: action.payload
      }
    },
    removeCurrentSelectedTiles: (state, action) => {
      return {
        ...state,
        currentSelectedTiles: initialState.currentSelectedTiles
      }
    },
    stopAutoBet: (state, action) => {
      return {
        ...state,
        loading: false,
        autoBetNoOfRounds: initialState.autoBetNoOfRounds,
        isAutoPlayStarted: initialState.isAutoPlayStarted,
        autoBetSuccess: initialState.autoBetSuccess,
        autoBetCurrentPlacedBet: initialState.autoBetCurrentPlacedBet,
        isLastLoss: false,
        isLastWin: false
      }
    },
    setMinesVolume: (state, action) => ({ ...state, minesVolume: action.payload })
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBbCrossGamePreviousRoundState.pending, (state, action) => {
        return {
          ...state,
          loading: true
        }
      })
      .addCase(getBbCrossGamePreviousRoundState.fulfilled, (state, action) => {
        if (action.payload?.hasUnfinishedGame) {
          const currentSelectedTiles = action.payload?.unfinishedGameBetDetails?.openTiles

          return {
            ...state,
            hasPreviousRoundCalled: true,
            loading: false,
            currentSelectedTiles,
            currentPlacedBet: action?.payload?.unfinishedGameBetDetails,
            betLock: true
          }
        }

        return {
          ...state,
          hasPreviousRoundCalled: true,
          loading: false
        }
      })
      .addCase(getBbCrossGamePreviousRoundState.rejected, (state, action) => {
        return {
          ...state,
          loading: false
        }
      })
      .addCase(bbCrossGamePlacedBet.pending, (state, action) => {
        return {
          ...state,
          loading: true
        }
      })
      .addCase(bbCrossGamePlacedBet.fulfilled, (state, action) => {
        const totalWagered = getPrecision(state.liveStats.totalWagered + parseFloat(action.payload.betAmount), 2)
        return {
          ...state,
          loading: false,
          currentPlacedBet: action.payload,
          betLock: true,
          liveStats: { ...state.liveStats, totalWagered }
        }
      })
      .addCase(bbCrossGamePlacedBet.rejected, (state, action) => {
        return {
          ...state,
          loading: false
        }
      })
      .addCase(foggyCrossGameAutoBet.pending, (state, action) => {
        return {
          ...state,
          autoBetSuccess: false,
          isAutoPlayStarted: true,
          autoBetCurrentPlacedBet: initialState.autoBetCurrentPlacedBet,
          loading: true,
          isLastWin: false,
          isLastLoss: false
        }
      })
      .addCase(foggyCrossGameAutoBet.fulfilled, (state, action) => {
        const truthyStrategyInputs = pickBy(state.strategyInputs, value => Boolean(value) === true)
        let isStop = false

        let _netReturn = state.strategyWatchers.netReturn

        const _previousBetAmount = +(action.payload?.betAmount ?? 0)

        let _nextBetAmount = +(action.payload?.betAmount ?? 0)

        const _winningAmount = +(action.payload?.winningAmount ?? 0)

        if (action.payload?.result === BET_RESULT.WON) {
          _netReturn = getPrecision(plus(_netReturn, minus(_winningAmount, _previousBetAmount)))
        } else if (action.payload?.result === BET_RESULT.LOST) {
          _netReturn = getPrecision(minus(_netReturn, _previousBetAmount))
        }

        if (action.payload?.result === BET_RESULT.WON && state.strategyInputs.onWinEnabled) {
          const winPercentage = +state.strategyInputs.onWinIncreasePercent ?? 0
          _nextBetAmount = getPrecision(plus(_previousBetAmount, _previousBetAmount * winPercentage / 100))
        } else if (action.payload?.result === BET_RESULT.LOST && state.strategyInputs.onLossEnabled) {
          const lossPercentage = +state.strategyInputs.onLossIncreasePercent ?? 0
          _nextBetAmount = getPrecision(plus(_previousBetAmount, _previousBetAmount * lossPercentage / 100))
        }

        if (
          (action.payload?.result === BET_RESULT.LOST && !state.strategyInputs.onLossEnabled) ||
          (action.payload?.result === BET_RESULT.WON && !state.strategyInputs.onWinEnabled)) {
          _nextBetAmount = state.strategyWatchers.initialAutoBetAmount
        }

        const _strategyWatchers = {
          totalBetAmount: getPrecision(plus(state.strategyWatchers.totalBetAmount, _previousBetAmount)),
          netReturn: _netReturn,
          autoBetAmount: _nextBetAmount,
          initialAutoBetAmount: state.strategyWatchers.initialAutoBetAmount
        }

        Object.keys(truthyStrategyInputs).forEach(strategy => {
          switch (strategy) {
            case 'stopOnProfit':
              isStop = _netReturn >= truthyStrategyInputs[strategy]
              break
            case 'stopOnLoss':
              isStop = Math.abs(_netReturn) >= truthyStrategyInputs[strategy]
              break
            case 'maxBetAmount':
              isStop = _strategyWatchers.totalBetAmount >= truthyStrategyInputs[strategy]
              break
          }
        })

        const totalProfit = getPrecision(state.liveStats.totalProfit - parseFloat(action.payload.betAmount) + parseFloat(action.payload.winningAmount), 2)
        const totalWagered = getPrecision(state.liveStats.totalWagered + parseFloat(action.payload.betAmount), 2)
        let totalLost = state.liveStats.totalLost
        let totalWins = state.liveStats.totalWins

        if (action.payload.result === BET_RESULT.WON) {
          totalWins = state.liveStats.totalWins + 1
        } else {
          totalLost = state.liveStats.totalLost + 1
        }
        const updatedLiveStates = { totalProfit, totalWagered, totalLost, totalWins }

        if ((state.autoBetNoOfRounds !== DEFAULT_MINE_GAME_AUTOBET_ROUNDS && +state.autoBetNoOfRounds - 1 === 0) || isStop) {
          return {
            ...state,
            loading: false,
            autoBetSuccess: true,
            isAutoPlayStarted: false,
            autoBetCurrentPlacedBet: action.payload,
            autoBetNoOfRounds: initialState.autoBetNoOfRounds,
            strategyWatchers: { ...initialState.strategyWatchers, autoBetAmount: _strategyWatchers.autoBetAmount },
            currentSelectedTiles: initialState.currentSelectedTiles,
            gameEnd: true,
            liveStats: updatedLiveStates
          }
        }
        return {
          ...state,
          autoBetSuccess: true,
          loading: false,
          autoBetCurrentPlacedBet: action.payload,
          autoBetNoOfRounds: state.autoBetNoOfRounds === DEFAULT_MINE_GAME_AUTOBET_ROUNDS ? state.autoBetNoOfRounds : state.autoBetNoOfRounds - 1,
          strategyWatchers: _strategyWatchers,
          gameEnd: true,
          liveStats: updatedLiveStates
        }
      })
      .addCase(foggyCrossGameAutoBet.rejected, (state, action) => {
        return {
          ...state,
          loading: false,
          autoBetSuccess: false,
          isAutoPlayStarted: false,
          autoBetNoOfRounds: initialState.autoBetNoOfRounds,
          isLastWin: false,
          isLastLoss: false
        }
      })
      .addCase(getMyBetsCrossGame.pending, (state, action) => {
        return {
          ...state,
          myBetsDataLoading: true
        }
      })
      .addCase(getMyBetsCrossGame.fulfilled, (state, action) => {
        return {
          ...state,
          myBetsDataLoading: false,
          myBetsData: action.payload.rows
        }
      })
      .addCase(getMyBetsCrossGame.rejected, (state, action) => {
        return {
          ...state,
          myBetsDataLoading: false
        }
      })
      .addCase(bbGameOpenTile.pending, (state, action) => {
        return {
          ...state,
          loading: true,
          openTileRequestId: action.meta.arg.requestId
        }
      })
      .addCase(bbGameOpenTile.fulfilled, (state, action) => {
        if (action.meta.arg.requestId !== state.openTileRequestId) {
          return state
        }

        // if (action.payload.mineTile) {
        //   const totalProfit = getPrecision(state.liveStats.totalProfit - parseFloat(action.payload.betAmount), 2)
        //   const totalLost = state.liveStats.totalLost + 1
        //   const isAutoWin = !action.payload.mineTile && Number(action.payload.winningAmount) > 0
        //   if (isAutoWin) {
        //     const totalProfit = getPrecision(
        //       state.liveStats.totalProfit - parseFloat(action.payload.betAmount) + Number(action.payload.winningAmount), 2
        //     )
        //     return {
        //       ...state,
        //       currentPlacedBet: action.payload,
        //       currentSelectedTiles: [...state.currentSelectedTiles, action.payload.tile],
        //       myBetsData: [action.payload, ...state.myBetsData],
        //       gameEnd: true,
        //       loading: false,
        //       openTileRequestId: null,
        //       liveStats: { ...state.liveStats, totalWins: state.liveStats.totalWins + 1, totalProfit }
        //     }
        //   }
        // }
        // return {
        //   ...state,
        //   currentSelectedTiles: [...state.currentSelectedTiles, action.payload.tile],
        //   loading: false,
        //   openTileRequestId: null
        // }

        const { mineTile, winningAmount, betAmount, tile } = action.payload
        const isAutoWin = !mineTile && Number(winningAmount) > 0

        if (mineTile) {
          const totalProfit = getPrecision(
            state.liveStats.totalProfit - parseFloat(betAmount), 2
          )
          return {
            ...state,
            currentPlacedBet: action.payload,
            currentSelectedTiles: [...state.currentSelectedTiles, tile],
            myBetsData: [action.payload, ...state.myBetsData],
            gameEnd: true,
            loading: false,
            openTileRequestId: null,
            liveStats: {
              ...state.liveStats,
              totalLost: state.liveStats.totalLost + 1,
              totalProfit
            }
          }
        }

        // Last safe tile opened, backend already settled the bet
        if (isAutoWin) {
          const totalProfit = getPrecision(
            state.liveStats.totalProfit - parseFloat(betAmount) + Number(winningAmount), 2
          )
          return {
          ...state,
          currentPlacedBet: action.payload,
          currentSelectedTiles: [...state.currentSelectedTiles, tile],
          myBetsData: [action.payload, ...state.myBetsData],
          gameEnd: true,
          loading: false,
          openTileRequestId: null,
          liveStats: {
            ...state.liveStats,
            totalWins: state.liveStats.totalWins + 1,
            totalProfit
          }
        }
      }

        // Normal safe tile
        return {
          ...state,
          currentSelectedTiles: [...state.currentSelectedTiles, tile],
          loading: false,
          openTileRequestId: null
        }
      })
      .addCase(bbGameOpenTile.rejected, (state, action) => {
        if (action.meta.arg.requestId !== state.openTileRequestId) {
          return state
        }

        return {
          ...state,
          loading: false,
          isLastWin: false,
          openTileRequestId: null
        }
      })
      .addCase(bbCrossGameCashOut.pending, (state, action) => {
        return {
          ...state,
          loading: true
        }
      })
      .addCase(bbCrossGameCashOut.fulfilled, (state, action) => {
        const totalProfit = getPrecision(state.liveStats.totalProfit - parseFloat(action.payload.betAmount) + action.payload.winningAmount, 2)
        return {
          ...state,
          currentPlacedBet: action.payload,
          myBetsData: [action.payload, ...state.myBetsData],
          loading: false,
          gameEnd: true,
          liveStats: { ...state.liveStats, totalWins: state.liveStats.totalWins + 1, totalProfit }
        }
      })
      .addCase(bbCrossGameCashOut.rejected, (state, action) => {
        return {
          ...state,
          loading: false
        }
      })
      .addCase(bbCrossGameAllBets.pending, (state, action) => {
        return {
          ...state,
          loading: true
        }
      })
      .addCase(bbCrossGameAllBets.fulfilled, (state, action) => {
        return {
          ...state,
          allBetsData: action.payload?.rows,
          loading: false
        }
      })
      .addCase(bbCrossGameAllBets.rejected, (state, action) => {
        return {
          ...state,
          loading: false
        }
      })
      .addCase(bbCrossGameTopBets.pending, (state, action) => {
        return {
          ...state,
          loading: true
        }
      })
      .addCase(bbCrossGameTopBets.fulfilled, (state, action) => {
        return {
          ...state,
          topBetsData: action.payload.sort(
            (a, b) => Number(b.winningAmount) - Number(a.winningAmount)
          ),
          loading: false
        }
      })
      .addCase(bbCrossGameTopBets.rejected, (state, action) => {
        return {
          ...state,
          loading: false
        }
      })
  }
})

export default reducer

export {
  stopAutoBet,
  setStrategyInputs,
  setAutoBetNoOfRounds,
  setInitialAutoBetAmount,
  setAutoPlayEndAfterAnimation,
  callBbCrossGameRoundEnd,
  addCurrentSelectedTiles,
  removeCurrentSelectedTiles,
  setAutoBetAmount,
  setRoundResult,
  setLiveStats,
  resetLiveStats,
  setMinesVolume
}
